'use client';

import React, { useEffect, useRef } from 'react';
import { useTheme } from 'next-themes';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (options: {
        url: string;
        parentElement: HTMLElement;
      }) => void;
    };
  }
}

const CalendlyWidget: React.FC = () => {
  const { resolvedTheme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);

  // Load the script once on component mount.
  useEffect(() => {
    if (document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]')) {
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // Initialize or re-initialize the widget when the theme changes or the container is ready.
  useEffect(() => {
    // A safe way to wait for the Calendly object to be ready.
    const checkAndInit = () => {
      if (window.Calendly && containerRef.current) {
        containerRef.current.innerHTML = ''; // Clear previous instance.
        const baseUrl = "https://calendly.com/medchartscan/medchartscan?hide_event_type_details=1&hide_gdpr_banner=1&hide_landing_page_details=1";
        const lightThemeParams = "background_color=f9fafb&text_color=020817&primary_color=3b82f6";
        const darkThemeParams = "background_color=121212&text_color=f9fafb&primary_color=3b82f6";
        const finalUrl = `${baseUrl}&${resolvedTheme === 'dark' ? darkThemeParams : lightThemeParams}`;

        window.Calendly.initInlineWidget({
          url: finalUrl,
          parentElement: containerRef.current,
        });
        return true; // Signal that we are done.
      }
      return false; // Not ready yet.
    };

    // If it's not ready right away, poll until it is.
    if (!checkAndInit()) {
      const intervalId = setInterval(() => {
        if (checkAndInit()) {
          clearInterval(intervalId);
        }
      }, 100);
      return () => clearInterval(intervalId);
    }
  }, [resolvedTheme]);

  return (
    <div ref={containerRef} className="min-w-[320px] h-full">
      {/* Show a placeholder only while the script is loading. */}
       <div className="w-full h-full bg-muted animate-pulse rounded-lg" />
    </div>
  );
};

export default CalendlyWidget;
